import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { ReportExportButton } from './report-export-button';

type App = 'TRAVEL' | 'TRADING';

const entryHref = (app: App, id: string) => (app === 'TRAVEL' ? `/travel/accounting/journal-entries/${id}` : `/trading/accounting/journal-entries/${id}`);

/** Yevmiye defteri: every posted entry of the period with its lines, in date order. */
export function JournalBookReport({ app }: { app: App }) {
  const key = app.toLowerCase();
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState(`${key}:accounting-reports:journal:startDate`, startOfMonth.toISOString().split('T')[0] ?? '');
  const [endDate, setEndDate] = useSessionState(`${key}:accounting-reports:journal:endDate`, today.toISOString().split('T')[0] ?? '');
  const [exporting, setExporting] = useState(false);

  const input = { startDate: new Date(startDate || startOfMonth), endDate: new Date(endDate || today) };
  const travel = trpc.financialReports.getJournalBook.useQuery(input, { enabled: app === 'TRAVEL' });
  const trading = trpc.tradingFinancialReports.getJournalBook.useQuery(input, { enabled: app === 'TRADING' });
  const query = app === 'TRAVEL' ? travel : trading;
  const data = query.data;
  const utils = trpc.useUtils();

  const exportBook = async () => {
    setExporting(true);
    try {
      const res = app === 'TRAVEL' ? await utils.financialReports.exportJournalBook.fetch(input) : await utils.tradingFinancialReports.exportJournalBook.fetch(input);
      downloadXlsx(res.rows, 'Yevmiye', `yevmiye-${startDate}-${endDate}-${dateStamp()}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4 print:hidden">
        <div className="space-y-2">
          <Label>Başlangıç / From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" />
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : query.error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{query.error.message}</div>
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {data.totals.entries} fiş · {data.totals.lines} satır · Borç {formatCurrency(data.totals.debit)} · Alacak {formatCurrency(data.totals.credit)}
            </p>
            <ReportExportButton onExport={exportBook} busy={exporting} disabled={data.entries.length === 0} />
          </div>

          {data.entries.length === 0 ? (
            <div className="bg-card rounded-lg border p-6 text-sm text-muted-foreground">Bu dönemde fiş yok. / No entries in this period.</div>
          ) : (
            <div className="bg-card overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">Fiş No</th>
                    <th className="px-4 py-2 font-medium">Tarih</th>
                    <th className="px-4 py-2 font-medium">Hesap</th>
                    <th className="px-4 py-2 font-medium">Açıklama</th>
                    <th className="px-4 py-2 text-right font-medium">Borç</th>
                    <th className="px-4 py-2 text-right font-medium">Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((entry) => (
                    <>
                      <tr key={entry.id} className="border-t bg-muted/30">
                        <td className="px-4 py-1.5">
                          <Link to={entryHref(app, entry.id)} className="font-mono text-xs font-semibold hover:underline">
                            {entry.entryNumber}
                          </Link>
                          {entry.isReversing && <span className="ml-2 text-[11px] text-muted-foreground">ters kayıt</span>}
                        </td>
                        <td className="px-4 py-1.5 whitespace-nowrap">{formatDate(entry.date)}</td>
                        <td className="px-4 py-1.5 text-xs text-muted-foreground">{entry.referenceType ?? ''}</td>
                        <td className="px-4 py-1.5 font-medium">{entry.description}</td>
                        <td className="px-4 py-1.5 text-right font-mono text-xs">{formatCurrency(entry.totalDebit)}</td>
                        <td className="px-4 py-1.5 text-right font-mono text-xs">{formatCurrency(entry.totalCredit)}</td>
                      </tr>
                      {entry.lines.map((l, i) => (
                        <tr key={`${entry.id}-${i}`} className="border-t">
                          <td className="px-4 py-1" />
                          <td className="px-4 py-1" />
                          <td className="px-4 py-1">
                            <span className="font-mono text-xs">{l.accountCode}</span> <span className="text-muted-foreground">{l.accountName}</span>
                          </td>
                          <td className="px-4 py-1 text-muted-foreground">{l.description ?? ''}</td>
                          <td className="px-4 py-1 text-right font-mono">{l.debit > 0 ? formatCurrency(l.debit) : ''}</td>
                          <td className="px-4 py-1 text-right font-mono">{l.credit > 0 ? formatCurrency(l.credit) : ''}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
